let ctx: AudioContext | null = null
let masterGain: GainNode | null = null
let sfxGain: GainNode | null = null
let ambientGain: GainNode | null = null
let narratorGain: GainNode | null = null
let resumed = false
const resumeListeners: Array<(c: AudioContext) => void> = []

export function initAudio(): void {
  if (ctx) return
  ctx = new AudioContext()

  masterGain = ctx.createGain()
  masterGain.gain.value = 1
  masterGain.connect(ctx.destination)

  sfxGain = ctx.createGain()
  sfxGain.gain.value = 1
  sfxGain.connect(masterGain)

  // Continuous beds (drone, pads, heartbeat — engine/ambient-audio.ts) hang
  // off their own bus so narrator ducking and mix levels steer them as one.
  ambientGain = ctx.createGain()
  ambientGain.gain.value = 1
  ambientGain.connect(masterGain)

  narratorGain = ctx.createGain()
  narratorGain.gain.value = 0.7
  narratorGain.connect(masterGain)

  document.addEventListener(
    'pointerdown',
    () => {
      ctx?.resume().then(() => {
        resumed = true
        for (const l of resumeListeners) l(ctx!)
        resumeListeners.length = 0
      })
    },
    { once: true },
  )
}

export function whenAudioReady(): Promise<AudioContext> {
  return new Promise(resolve => {
    if (resumed && ctx) {
      resolve(ctx)
      return
    }
    if (!ctx) initAudio()
    if (resumed && ctx) resolve(ctx)
    else resumeListeners.push(resolve)
  })
}

/** Plays a decoded voice-line buffer (narrator or mother dialog) on the shared
 *  voice bus, ducking SFX/ambient under it. No-op if audio isn't unlocked yet —
 *  callers already have `buffer.duration` for caption/UI timing regardless. */
export function playVoiceBuffer(buffer: AudioBuffer): void {
  if (!ctx || !narratorGain || !sfxGain) return
  const t = ctx.currentTime
  const src = ctx.createBufferSource()
  src.buffer = buffer
  src.connect(narratorGain)
  src.start(t)

  // Duck SFX and ambient under the voice: dip over 150ms, restore over 300ms
  // after the buffer ends. cancelScheduledValues + setValueAtTime guards
  // against overlap with a previous line still finishing its restore ramp.
  for (const [bus, duckTo] of [[sfxGain, 0.6], [ambientGain, 0.45]] as const) {
    if (!bus) continue
    bus.gain.cancelScheduledValues(t)
    bus.gain.setValueAtTime(bus.gain.value, t)
    bus.gain.linearRampToValueAtTime(duckTo, t + 0.15)
    bus.gain.setValueAtTime(duckTo, t + buffer.duration)
    bus.gain.linearRampToValueAtTime(1.0, t + buffer.duration + 0.3)
  }
}

/** The ambient bus, once initAudio has run — engine/ambient-audio.ts builds
 *  its long-lived graph on this. Null before init (tests, SSR). */
export function ambientBus(): { ctx: AudioContext; bus: GainNode } | null {
  if (!ctx || !ambientGain) return null
  return { ctx, bus: ambientGain }
}

/** Stereo pan for a one-shot emitted at world-x `emitterX` while the player
 *  (≈ screen center) is at `playerX`. Full pan at ~2.5 world units. */
export function panFor(emitterX: number, playerX: number): number {
  return Math.max(-1, Math.min(1, (emitterX - playerX) / 2.5)) * 0.8
}

// Shared white-noise loop — one allocation for every noise-based sound (dash
// whoosh, ambient rumble and wash, water rush).
//
// The length is a bed concern, not a one-shot one. A whoosh reads 200ms and is
// gone; the ambient layers loop this forever through a lowpass, and a lowpass
// is what makes a short loop audible as a rhythm — filter noise down to ~100 Hz
// and what's left is slow undulation, so a 2s loop repeated the same swell
// every 2 seconds. Eight seconds of it costs ~1.5 MB at 48 kHz and puts any
// residual period well past the point where the ear can hold the pattern.
export const NOISE_SECONDS = 8
let sharedNoise: AudioBuffer | null = null
export function noiseBuffer(c: AudioContext): AudioBuffer {
  if (sharedNoise && sharedNoise.sampleRate === c.sampleRate) return sharedNoise
  const len = c.sampleRate * NOISE_SECONDS
  const buf = c.createBuffer(1, len, c.sampleRate)
  const d = buf.getChannelData(0)
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1
  sharedNoise = buf
  return buf
}

export type SfxName =
  | 'lunge_tick' | 'dash_impact' | 'engulf_swell' | 'ally_kill' | 'eat' | 'orb_collect'
  | 'evolve_inhale' | 'evolve_burst' | 'dash_whoosh' | 'shield_up' | 'speciate_shimmer'

export function playSfx(name: SfxName, opts: { volume?: number; pan?: number } = {}): void {
  if (!ctx || !sfxGain) return
  const t = ctx.currentTime
  const vol = opts.volume ?? 0.5
  const gain = ctx.createGain()
  gain.gain.setValueAtTime(0, t)
  if (opts.pan !== undefined && opts.pan !== 0 && typeof ctx.createStereoPanner === 'function') {
    const panner = ctx.createStereoPanner()
    panner.pan.setValueAtTime(Math.max(-1, Math.min(1, opts.pan)), t)
    gain.connect(panner)
    panner.connect(sfxGain)
  } else {
    gain.connect(sfxGain)
  }

  if (name === 'lunge_tick') {
    const osc = ctx.createOscillator()
    osc.type = 'square'
    osc.frequency.setValueAtTime(800, t)
    osc.frequency.exponentialRampToValueAtTime(400, t + 0.08)
    gain.gain.linearRampToValueAtTime(vol * 0.6, t + 0.005)
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.09)
    osc.connect(gain)
    osc.start(t)
    osc.stop(t + 0.1)
  } else if (name === 'dash_impact') {
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(160, t)
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.12)
    gain.gain.linearRampToValueAtTime(vol, t + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.16)
    osc.connect(gain)
    osc.start(t)
    osc.stop(t + 0.18)
  } else if (name === 'ally_kill') {
    // Sharp mid-range pop — ally charge lands a kill.
    const osc = ctx.createOscillator()
    osc.type = 'square'
    osc.frequency.setValueAtTime(520, t)
    osc.frequency.exponentialRampToValueAtTime(180, t + 0.07)
    gain.gain.linearRampToValueAtTime(vol * 0.5, t + 0.006)
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.12)
    osc.connect(gain)
    osc.start(t)
    osc.stop(t + 0.13)
  } else if (name === 'eat') {
    // Comical phoomp — low suction pop with randomised pitch.
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    const pitchVariance = 0.85 + Math.random() * 0.3  // ±15 %
    osc.frequency.setValueAtTime(180 * pitchVariance, t)
    osc.frequency.exponentialRampToValueAtTime(50 * pitchVariance, t + 0.10)
    gain.gain.linearRampToValueAtTime(vol, t + 0.008)
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.13)
    osc.connect(gain)
    osc.start(t)
    osc.stop(t + 0.14)
  } else if (name === 'orb_collect') {
    // Bright crystalline ping — two stacked sines, fast attack, medium ring-out.
    const osc1 = ctx.createOscillator()
    const osc2 = ctx.createOscillator()
    osc1.type = 'sine'
    osc2.type = 'sine'
    osc1.frequency.setValueAtTime(880, t)
    osc1.frequency.exponentialRampToValueAtTime(660, t + 0.18)
    osc2.frequency.setValueAtTime(1320, t)
    osc2.frequency.exponentialRampToValueAtTime(990, t + 0.18)
    gain.gain.linearRampToValueAtTime(vol * 0.55, t + 0.006)
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.32)
    osc1.connect(gain)
    osc2.connect(gain)
    osc1.start(t); osc1.stop(t + 0.34)
    osc2.start(t); osc2.stop(t + 0.34)
  } else if (name === 'dash_whoosh') {
    // Quick water-swish on dash activation — bandpassed noise sweep. The
    // impact (if any) still lands separately as dash_impact.
    const src = ctx.createBufferSource()
    src.buffer = noiseBuffer(ctx)
    const filt = ctx.createBiquadFilter()
    filt.type = 'bandpass'
    filt.Q.value = 1.2
    filt.frequency.setValueAtTime(400, t)
    filt.frequency.exponentialRampToValueAtTime(1800, t + 0.12)
    gain.gain.linearRampToValueAtTime(vol * 0.5, t + 0.015)
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2)
    src.connect(filt)
    filt.connect(gain)
    src.start(t)
    src.stop(t + 0.22)
  } else if (name === 'shield_up') {
    // Membrane snapping taut — quick upward gliss into a held shimmer.
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(320, t)
    osc.frequency.exponentialRampToValueAtTime(640, t + 0.09)
    gain.gain.linearRampToValueAtTime(vol * 0.45, t + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35)
    osc.connect(gain)
    osc.start(t)
    osc.stop(t + 0.37)
  } else if (name === 'evolve_inhale') {
    // The gather before the tier-up burst: low warm swell rising in pitch
    // and brightness across the anticipation phase (~1s to the burst frame),
    // cut just before the burst sound takes over. Body (saw through an
    // opening lowpass) + a thin octave shimmer fading in above it.
    const osc = ctx.createOscillator()
    const filt = ctx.createBiquadFilter()
    osc.type = 'sawtooth'
    filt.type = 'lowpass'
    osc.frequency.setValueAtTime(90, t)
    osc.frequency.exponentialRampToValueAtTime(220, t + 0.95)
    filt.frequency.setValueAtTime(180, t)
    filt.frequency.exponentialRampToValueAtTime(1600, t + 0.95)
    gain.gain.linearRampToValueAtTime(vol * 0.5, t + 0.6)
    gain.gain.linearRampToValueAtTime(vol * 0.65, t + 0.92)
    gain.gain.linearRampToValueAtTime(0.0001, t + 1.0)
    osc.connect(filt)
    filt.connect(gain)
    osc.start(t)
    osc.stop(t + 1.02)

    const shimmer = ctx.createOscillator()
    const shimmerGain = ctx.createGain()
    shimmer.type = 'sine'
    shimmer.frequency.setValueAtTime(660, t)
    shimmer.frequency.exponentialRampToValueAtTime(880, t + 0.95)
    shimmerGain.gain.setValueAtTime(0, t)
    shimmerGain.gain.linearRampToValueAtTime(vol * 0.12, t + 0.9)
    shimmerGain.gain.linearRampToValueAtTime(0.0001, t + 1.0)
    shimmer.connect(shimmerGain)
    shimmerGain.connect(sfxGain)
    shimmer.start(t)
    shimmer.stop(t + 1.02)
  } else if (name === 'evolve_burst') {
    // The fanfare proper — warm major chord blooming at the burst frame,
    // slight detune per voice so it reads hand-played rather than synthetic,
    // plus a low thump for weight. Picture-book brass-adjacent, never harsh.
    const chord = [261.63, 329.63, 392.0, 523.25] // C4 E4 G4 C5
    const weights = [0.9, 0.7, 0.75, 0.4]
    for (let i = 0; i < chord.length; i++) {
      const osc = ctx.createOscillator()
      const g = ctx.createGain()
      osc.type = 'triangle'
      const detune = 1 + (Math.random() * 2 - 1) * 0.004
      osc.frequency.setValueAtTime(chord[i] * detune, t)
      g.gain.setValueAtTime(0, t)
      g.gain.linearRampToValueAtTime(vol * 0.32 * weights[i], t + 0.015 + i * 0.012)
      g.gain.exponentialRampToValueAtTime(0.001, t + 1.3 + i * 0.1)
      osc.connect(g)
      g.connect(sfxGain)
      osc.start(t)
      osc.stop(t + 1.5)
    }
    // Sub thump — the body arriving in its new size.
    const thump = ctx.createOscillator()
    thump.type = 'sine'
    thump.frequency.setValueAtTime(140, t)
    thump.frequency.exponentialRampToValueAtTime(48, t + 0.22)
    gain.gain.linearRampToValueAtTime(vol * 0.8, t + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4)
    thump.connect(gain)
    thump.start(t)
    thump.stop(t + 0.42)
  } else if (name === 'speciate_shimmer') {
    // Speciation reveal — a bright ascending arpeggio layered over the
    // evolve_burst chord: the "what did I become?" sparkle. Pentatonic run
    // so it never clashes with the C-major chord underneath; staggered
    // starts read as a run, not a cluster. Picture-book celesta-adjacent.
    const run = [523.25, 659.25, 783.99, 880.0, 1046.5] // C5 E5 G5 A5 C6
    for (let i = 0; i < run.length; i++) {
      const osc = ctx.createOscillator()
      const g = ctx.createGain()
      osc.type = 'sine'
      const detune = 1 + (Math.random() * 2 - 1) * 0.003
      const start = t + 0.06 + i * 0.07
      osc.frequency.setValueAtTime(run[i] * detune, start)
      g.gain.setValueAtTime(0, start)
      g.gain.linearRampToValueAtTime(vol * 0.30 * (1 - i * 0.08), start + 0.012)
      g.gain.exponentialRampToValueAtTime(0.001, start + 0.8)
      osc.connect(g)
      g.connect(sfxGain)
      osc.start(start)
      osc.stop(start + 0.85)
    }
    gain.gain.setValueAtTime(0, t) // shared gain unused; voices route directly
  } else if (name === 'engulf_swell') {
    const osc = ctx.createOscillator()
    osc.type = 'sawtooth'
    const filt = ctx.createBiquadFilter()
    filt.type = 'lowpass'
    filt.frequency.setValueAtTime(200, t)
    filt.frequency.exponentialRampToValueAtTime(1200, t + 0.3)
    osc.frequency.setValueAtTime(80, t)
    osc.frequency.linearRampToValueAtTime(140, t + 0.3)
    gain.gain.linearRampToValueAtTime(vol * 0.7, t + 0.08)
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.32)
    osc.connect(filt)
    filt.connect(gain)
    osc.start(t)
    osc.stop(t + 0.34)
  }
}
